<template>
  <v-card color="#3A3A3A" class="fill-height dialog-container">
    <v-container fluid class="pa-0 fill-height">
      <v-row no-gutters class="fill-height">
        <v-col sm="10" class="fill-height">
          <div class="editor fill-height">
            <v-container fluid class="fill-height" style="padding-bottom: 32px">
              <v-row>
                <MainMenu :editor="editor" />
              </v-row>
              <v-row style="overflow-y: scroll" class="fill-height pa-4 px-3">
                <v-container
                  class="ma-0 pa-0"
                  style="background-color: white"
                  fluid
                >
                  <ChapterThumbnail
                    :chapterId="chapter.id"
                    :initialThumbnail="chapter.featuredMediaObject"
                  />
                  <v-row>
                    <v-col class="text-left"
                      ><v-sheet class="rounded-lg pa-3 ma-5" color="background">
                        <v-row align="center">
                          <v-col cols="auto">
                            <span style="font-size: 1.4em; color: #636262"
                              >{{ $t("chapter.chapterTitle") }}:
                            </span>
                          </v-col>
                          <v-col>
                            <UpdateChapterTitleTextField
                              :iri="chapter.id"
                              :name="chapter.name"
                            />
                          </v-col>
                        </v-row>
                      </v-sheet>
                    </v-col>
                  </v-row>
                  <v-row class="pa-3">
                    <EditorContent
                      class="editor__content"
                      :editor="editor"
                      :style="'font-size: ' + zoomEm + 'em'"
                    />
                  </v-row>
                </v-container>
              </v-row>
            </v-container>
            <div style="position: fixed; bottom: 0px; width: 83%">
              <div class="floating-menu-container rounded-xl">
                <v-menu top offset-y elevation="0">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primaryAction" dark v-bind="attrs" v-on="on">
                      <v-icon left>mdi-plus</v-icon
                      >{{ $t("chapter.addContent") }}
                    </v-btn>
                  </template>
                  <v-list class="addcontentlist">
                    <v-list-item>
                      <v-btn @click="addAudio" color="secondaryBackground"
                        ><v-icon left>mdi-music-note-eighth</v-icon
                        >{{ $t("chapter.audio.audioTrack") }}</v-btn
                      >
                    </v-list-item>
                    <v-list-item>
                      <v-btn @click="addImage" color="secondaryBackground"
                        ><v-icon left>mdi-image-outline</v-icon
                        >{{ $t("chapter.image.image") }}</v-btn
                      >
                    </v-list-item>
                    <v-list-item>
                      <v-btn @click="addVideo" color="secondaryBackground"
                        ><v-icon left>mdi-video-box</v-icon
                        >{{ $t("chapter.video.videoFrame") }}</v-btn
                      >
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-btn color="#6A6969" class="white--text" @click="resetZoom">{{
                  $t("chapter.zoom100")
                }}</v-btn>
                <v-btn
                  color="#6A6969"
                  class="white--text"
                  fab
                  x-small
                  @click="incrementZoom"
                  ><v-icon>mdi-magnify-plus-outline</v-icon></v-btn
                ><v-btn
                  color="#6A6969"
                  class="white--text"
                  x-small
                  fab
                  @click="decrementZoom"
                  ><v-icon>mdi-magnify-minus-outline</v-icon></v-btn
                >
              </div>
            </div>
          </div>
        </v-col>
        <v-col sm="2">
          <Sidebar :editor="editor" />
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import MainMenu from "@/components/TextEditor/MainMenu";
import EditableImage from "@/tiptap/extensions/editable-image/editable-image";
import Video from "@/tiptap/extensions/video/video";
import Audio from "@/tiptap/extensions/audio/audio";
import Info from "@/tiptap/extensions/info/info";
import Sidebar from "@/components/TextEditor/Sidebar";
import TextAlign from "@tiptap/extension-text-align";
import ChapterThumbnail from "./ChapterThumbnail";
import Placeholder from "@tiptap/extension-placeholder";
import UpdateChapterTitleTextField from "@/components/CreatorTools/StoryHub/ChapterList/UpdateChapterTitleTextField";

import i18n from "@/i18n";
const ZOOM_EM_STEPS = 0.5;
const START_ZOOM = 2;
const MAX_ZOOM = 10;
const EXTENSIONS = [
  StarterKit,
  TextAlign.configure({
    types: ["heading", "paragraph", "image"],
  }),
  EditableImage,
  Video,
  Audio,
  Info,
  Placeholder.configure({ placeholder: i18n.t("chapter.placeholder") }),
];

export default {
  components: {
    ChapterThumbnail,
    EditorContent,
    MainMenu,
    Sidebar,
    UpdateChapterTitleTextField,
  },
  props: {
    value: String,
    initialText: String,
    chapter: Object,
  },
  data() {
    return {
      editor: null,
      zoomLevel: START_ZOOM,
    };
  },
  mounted() {
    let vm = this;
    this.editor = new Editor({
      content: this.initialText,
      onUpdate({ editor }) {
        vm.$emit("input", editor.getHTML());
      },
      extensions: EXTENSIONS,
    });
  },
  methods: {
    incrementZoom() {
      if (this.zoomLevel == MAX_ZOOM) {
        return;
      }
      this.zoomLevel = this.zoomLevel + 1;
    },
    decrementZoom() {
      if (this.zoomLevel == 1) {
        return;
      }
      this.zoomLevel = this.zoomLevel - 1;
    },
    resetZoom() {
      this.zoomLevel = START_ZOOM;
    },
    addImage() {
      this.editor.chain().focus().setTextSelection(this.editor.state.selection.to).setImage({ src: null }).run();
    },
    addAudio() {
      this.editor.chain().focus().setTextSelection(this.editor.state.selection.to).setAudio().run();
    },
    addVideo() {
      this.editor.chain().focus().setTextSelection(this.editor.state.selection.to).setVideo().run();
    },
  },
  computed: {
    zoomEm() {
      return this.zoomLevel * ZOOM_EM_STEPS;
    },
  },
  watch: {
    initialText(newValue) {
      try {
        if (this.editor.getHTML() != newValue) {
          this.editor.commands.setContent(newValue);
        }
      } catch (e) {
        console.log(e);
      }
    },
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
};
</script>
<style scoped lang="scss">
.editor__content {
  width: 100%;
  background-color: white;
}
::v-deep img,
::v-deep audio,
::v-deep video {
  position: relative;
  width: 50%;
  height: 300px;
  margin: 0 auto;
  background-color: var(--v-accentElementBackground-base);
  border: 4px dashed var(--v-accentElement-base);
  line-height: 300px;
  cursor: pointer;
  font-size: 24px;
  color: var(--v-accentElement-base);
}
::v-deep audio {
  height: 100px;
}

::v-deep img[src] {
  width: initial;
  height: initial;
}
::v-deep video.ProseMirror-selectednode,
::v-deep audio.ProseMirror-selectednode,
::v-deep .ProseMirror-selectednode img {
  border: 4px dashed var(--v-primaryAction-base);
}
.dialog-container {
  height: calc(100% - 64px);
}
::v-deep span[info] {
  background: var(--v-secondaryBackground-base);
  border-radius: 4px;
  color: #008cff;
  padding: 2px 8px;
}

::v-deep .ProseMirror:focus {
  outline: none;
}

.addcontentlist.v-list {
  background-color: #777474;
}

.floating-menu-container {
  background-color: rgba(1, 1, 1, 0.5);
  display: inline-block;
  padding: 8px;
  button {
    margin: 0 5px;
  }
}

::v-deep .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #ced4da;
  pointer-events: none;
  height: 0;
}
</style>